import { IGenericObject } from 'models/base';

export const palette = {
  red: '#E60012',
  redGradient: 'linear-gradient(180deg, rgba(230,0,18,1) 0%, rgba(152,0,12,1) 100%)',
  darkRed: '#98000c',
  grey: '#707070',
  lightWhiteGrey: '#F5F5F5',
  lightGrey: '#D8D8D8',
  lighterGrey: '#edebe9',
  lightDarkGrey: '#333333',
  darkGrey: '#242424',
  black: '#000',
  white: '#ffffff',
  green: '#6bb700',
};

export const breakpoints = {
  xs: 320,
  mobile: 375,
  tablet: 768,
  desktop: 980,
  xl: 1920,
};

// Breakpoints
export const media = {
  smallerThanTablet: `@media (max-width: ${breakpoints.tablet - 1}px)`,
  xs: `@media (min-width: ${breakpoints.xs}px)`,
  mobile: `@media (min-width: ${breakpoints.mobile}px)`,
  tablet: `@media (min-width: ${breakpoints.tablet}px)`,
  desktop: `@media (min-width: ${breakpoints.desktop}px)`,
  xl: `@media (min-width: ${breakpoints.xl}px)`,
  notDesktop: `@media (max-width: ${breakpoints.desktop - 1}px)`,
  landscapeTablet: `@media (device-width: 768px) and (device-height: 1024px) and (orientation:landscape)`,
};

export const responsiveBackground = {
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  [`@media (max-aspect-ratio: 3/4)`]: {
    backgroundPosition: 'center',
    backgroundImage:
      'url("https://ts-cms-production.votenow.tv/campaign/10/08/1008023/1616716820605d24148777d5.89604988.png")',
  },
  [`@media (min-aspect-ratio: 3/4)`]: {
    backgroundImage:
      'url("https://ts-cms-production.votenow.tv/campaign/10/08/1008023/1616716820605d24148777d5.89604988.png")',
  },
  [`@media (min-aspect-ratio: 1/1)`]: {
    backgroundImage:
      'url("https://ts-cms-production.votenow.tv/campaign/10/08/1008023/1619792884608c13f4678015.17874895.jpg")',
  },
  [`@media (min-aspect-ratio: 16/9)`]: {
    backgroundImage:
      'url("https://ts-cms-production.votenow.tv/campaign/10/08/1008023/1619792878608c13eef32716.02263392.jpg")',
  },
  [`@media (min-aspect-ratio: 16/5)`]: {
    backgroundImage:
      'url("https://ts-cms-production.votenow.tv/campaign/10/08/1008023/1619792864608c13e01fb794.47776977.jpg")',
  },
  [`@media (min-aspect-ratio: 48/13)`]: {
    backgroundImage:
      'url("https://ts-cms-production.votenow.tv/campaign/10/08/1008023/1619792878608c13eef32716.02263392.jpg")',
  },
};

const OIPLightBackgrounds = {
  desktop: 'https://ts-cms-production.votenow.tv/campaign/10/08/1008743/1629274461611cc15d38d724.56940662.jpg',
  tablet: 'https://ts-cms-production.votenow.tv/campaign/10/08/1008743/1629274472611cc16817bcb9.56842091.jpg',
  mobile: 'https://ts-cms-production.votenow.tv/campaign/10/08/1008743/1629274467611cc1635c0dc8.05221007.jpg',
};

const OIPDarkBackgrounds = {
  desktop: 'https://ts-cms-production.votenow.tv/campaign/10/08/1008743/16297982686124bf7ce02c79.04011219.jpg',
  tablet: 'https://ts-cms-production.votenow.tv/campaign/10/08/1008743/16297982646124bf7883e6c7.02144917.jpg',
  mobile: 'https://ts-cms-production.votenow.tv/campaign/10/08/1008743/16297982586124bf72ba77a6.85762913.jpg',
};

export const OIPResponsiveBackground = (options: { opacity: 'none' | 'dark' | 'light'; fixed: boolean }) => {
  let rgbOpacity: any;
  const fixed = options.fixed ? 'fixed' : '';
  const backgrounds = OIPDarkBackgrounds;
  switch (options.opacity) {
    case 'dark':
      rgbOpacity = {
        desktop: 'rgba(0,0,0,0.65)',
        tablet: 'rgba(0,0,0,0.5)',
        mobile: 'rgba(0,0,0,0.4)',
      };
      break;
    case 'light':
      rgbOpacity = {
        desktop: 'rgba(0,0,0,0)',
        tablet: 'rgba(0,0,0,0)',
        mobile: 'rgba(0,0,0,0)',
      };
      break;
    default:
      rgbOpacity = {
        desktop: 'rgba(0,0,0,0)',
        tablet: 'rgba(0,0,0,0)',
        mobile: 'rgba(0,0,0,0)',
      };
      break;
  }

  return {
    [`@media (max-aspect-ratio: 64/137)`]: {
      background: `linear-gradient(${rgbOpacity.mobile}, ${rgbOpacity.mobile}), url("${backgrounds.mobile}") center center/cover no-repeat ${fixed}`,
    },
    [`@media (min-aspect-ratio: 64/137)`]: {
      background: `linear-gradient(${rgbOpacity.mobile}, ${rgbOpacity.mobile}), url("${backgrounds.mobile}") center center/cover no-repeat ${fixed}`,
    },
    [`@media (min-aspect-ratio: 3/4)`]: {
      background: `linear-gradient(${rgbOpacity.tablet}, ${rgbOpacity.tablet}), url("${backgrounds.tablet}") center center/cover no-repeat ${fixed}`,
    },
    [`@media (min-aspect-ratio: 4/3)`]: {
      background: `linear-gradient(${rgbOpacity.desktop}, ${rgbOpacity.desktop}), url("${backgrounds.desktop}") center center/cover no-repeat ${fixed}`,
    },
  };
};

export const appMaxWidth = '980px';

export const rootFontSize = '16px';

// Functions
export const normalizeStyles = (styles: IGenericObject) => {
  if (!styles) {
    return null;
  }

  return Object.keys(styles).reduce((storage, target) => {
    const store = Object.keys(styles[target]).reduce((store, property) => {
      if (!store[target]) {
        store[target] = {};
      }
      const normalizedProperty = property.replace(/_([a-z])/g, (text) => text[1].toUpperCase());

      const a11yStyle =
        normalizedProperty === 'fontSize' ? pxToRem(styles[target][property]) : styles[target][property];

      store[target][normalizedProperty] = a11yStyle;

      return store;
    }, Object.create(null));
    return { ...storage, ...store };
  }, Object.create(null));
};

export const children = (child: string) => {
  return `:nth-child(1n) ${child}`;
};

/* tslint:disable:no-bitwise */
export const darkenColor = (color: string) => {
  let hex = '';

  switch (color) {
    case undefined:
    case null:
    case '#000':
    case '#000000':
      break;
    case '#fff':
    case '#ffffff':
      hex = '#ccc';
      break;
    default:
      const percent = -0.2; // This can be passed as a parameter
      const f = parseInt(color.slice(1), 16);
      const t = percent < 0 ? 0 : 255;
      const p = percent < 0 ? percent * -1 : percent;
      const R = f >> 16;
      const G = (f >> 8) & 0x00ff;
      const B = f & 0x0000ff;
      hex =
        '#' +
        (
          0x1000000 +
          (Math.round((t - R) * p) + R) * 0x10000 +
          (Math.round((t - G) * p) + G) * 0x100 +
          (Math.round((t - B) * p) + B)
        )
          .toString(16)
          .slice(1);
      break;
  }

  return hex;
};
/* tslint:enable:no-bitwise */

export const pxToRem = (fontSize: string) => {
  return `${parseInt(fontSize, 10) / parseInt(rootFontSize, 10)}rem`;
};

export const applyAspectRatio = (aspectRatio = 16 / 9) => {
  const ratio = +(1 / aspectRatio).toFixed(4) * 100;
  const paddingBottom = ratio + '%';
  return { paddingBottom };
};
